/*
 * Session tokens, and the rotating-refresh flow.
 *
 * This module deliberately owns token storage AND does its own bare `fetch`
 * for `/auth/refresh`, rather than importing the API client. Two reasons:
 *
 *   1. `api.ts` imports this module to drive the 401 interceptor. If this
 *      module imported `api` back, the cycle would be real (not type-only).
 *   2. A refresh must never go through the interceptor. A 401 from
 *      `/auth/refresh` triggering another refresh is an infinite loop, and —
 *      see below — a retried refresh is exactly the replay that destroys the
 *      session.
 *
 * ── The hazard this file is shaped around ─────────────────────────────────
 *
 * Refresh tokens ROTATE and are SINGLE USE, and a reused one is treated as
 * theft: the server revokes EVERY live refresh token for that user, on every
 * device. A replay does not fail one request; it signs the person out of
 * everything.
 *
 * The server allows a 10-SECOND GRACE WINDOW: a token spent within the last
 * 10 seconds, whose recorded successor is still live, is answered rather than
 * punished. That covers the ordinary two-tab case, and it is why there is no
 * cross-tab lock here any more — there was one, and it is deleted rather than
 * kept "just in case", because a best-effort lock that no longer protects
 * anything is only a source of stale-state bugs. Past 10 seconds, or one hop
 * further down the chain, a reuse is theft again.
 *
 * A grace hit does NOT hand back the successor the other tab holds — only a
 * hash of each token is stored, so the raw value exists exactly once. The late
 * caller gets that successor ROTATED, i.e. a genuinely new pair. Whichever tab
 * refreshed last holds the live token, which is why every path here writes
 * what it receives straight back to shared storage.
 *
 * What remains, and why:
 *
 *   - One in-flight refresh promise per tab. This alone keeps a single tab out
 *     of the sub-second race entirely.
 *   - Exactly ONE retry, after re-reading shared storage. A 401 means either
 *     "you lost a sub-second race, nothing was revoked" or "everything is
 *     gone", and the server deliberately makes those indistinguishable — a
 *     specific reason would tell an attacker whether a token is unknown,
 *     expired or stolen. The handling is the same for both.
 *   - Never a retry loop. A loop is how you walk past the grace window and
 *     turn an honest race into a theft response.
 *
 * Access tokens last 5.28 days in production, NOT the 15 minutes the backend
 * repo's default suggests — which is why the expiry is taken from the server's
 * `expiresIn` rather than computed from any config value.
 */

const ACCESS_KEY = 'emerge.accessToken';
const REFRESH_KEY = 'emerge.refreshToken';
const REMEMBER_KEY = 'emerge.rememberMe';
const EXPIRY_KEY = 'emerge.accessTokenExpiry';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

function safely<T>(operation: () => T, fallback: T): T {
  try {
    return operation();
  } catch {
    // Private modes can throw on any storage access.
    return fallback;
  }
}

/*
 * Where the tokens live is the user's choice.
 *
 * "Keep me signed in" stores them in localStorage, so the session outlives the
 * browser. Unchecked, they go to sessionStorage instead and die with the tab —
 * which is what someone on a shared machine expects.
 *
 * Reads check session first: if both somehow hold a value, the one bound to
 * this tab is the more recent intent. Clearing always clears both, so a
 * sign-out can never leave a stray copy behind in the other store.
 */
export const tokenStorage = {
  get: (): string | null =>
    safely(() => sessionStorage.getItem(ACCESS_KEY) ?? localStorage.getItem(ACCESS_KEY), null),

  getRefresh: (): string | null =>
    safely(() => sessionStorage.getItem(REFRESH_KEY) ?? localStorage.getItem(REFRESH_KEY), null),

  /**
   * Store a token pair.
   *
   * `remember` is optional on purpose: a rotation must not move the tokens
   * between stores, so `refreshSession()` omits it and the previous choice is
   * read back from `getRemembered()`.
   */
  set: (
    accessToken: string,
    refreshToken: string | null,
    remember?: boolean,
    expiresIn?: number,
  ): void =>
    safely(() => {
      const keep = remember ?? tokenStorage.getRemembered();
      // Write one store, clear the other, so the two can never disagree.
      const [target, other] = keep ? [localStorage, sessionStorage] : [sessionStorage, localStorage];
      target.setItem(ACCESS_KEY, accessToken);
      other.removeItem(ACCESS_KEY);
      if (refreshToken !== null) {
        target.setItem(REFRESH_KEY, refreshToken);
        other.removeItem(REFRESH_KEY);
      }
      /*
       * Absolute deadline rather than the raw duration, so a tab that was
       * asleep does not schedule its refresh from the moment it woke up.
       */
      if (typeof expiresIn === 'number' && Number.isFinite(expiresIn)) {
        target.setItem(EXPIRY_KEY, String(Math.floor(Date.now() / 1000) + expiresIn));
      } else {
        target.removeItem(EXPIRY_KEY);
      }
      other.removeItem(EXPIRY_KEY);
      localStorage.setItem(REMEMBER_KEY, keep ? '1' : '0');
    }, undefined),

  clear: (): void =>
    safely(() => {
      for (const store of [localStorage, sessionStorage]) {
        store.removeItem(ACCESS_KEY);
        store.removeItem(REFRESH_KEY);
        store.removeItem(EXPIRY_KEY);
      }
    }, undefined),

  /** Last choice, so the sign-in form can restore the user's preference. */
  getRemembered: (): boolean => safely(() => localStorage.getItem(REMEMBER_KEY) !== '0', true),
};

/* ── Support sessions ───────────────────────────────────────────────────── */

/*
 * A super admin acting AS another user for 30 minutes, to reproduce what that
 * person sees.
 *
 * ── The one rule everything here is shaped around ────────────────────────
 *
 * **The support token is ADDITIONAL. It never replaces the base session.**
 *
 * `emerge.accessToken` and `emerge.refreshToken` are not touched — not
 * overwritten, not cleared, not rotated — for the whole life of a support
 * session. The support token lives under its own key, and `activeAccessToken()`
 * is the ONE place that decides which of the two a request carries. So ending a
 * support session is a local delete of one key: there is nothing to restore,
 * because nothing was taken away, and no path through this file can leave the
 * super admin signed out of their own account.
 *
 * The alternative — swap the base tokens out and swap them back — was rejected
 * outright. It makes "get me back to my own account" depend on a write that can
 * fail, at the exact moment something has already gone wrong.
 *
 * ── The support token MUST NOT ENTER THE REFRESH FLOW ────────────────────
 *
 * Refresh tokens rotate, are single use, and a reused one is read as theft: the
 * server revokes every live refresh token for that user on every device. See
 * the note at the top of this file.
 *
 * The exclusion here is STRUCTURAL, not a guard somebody can forget:
 *
 *   - a support session stores **no refresh token** — the field does not exist
 *     on {@link StoredSupportSession}, so there is nothing to rotate;
 *   - `refreshSession()` reads `REFRESH_KEY` and only `REFRESH_KEY`, which the
 *     support session never writes;
 *   - and a 401 while acting as somebody else does not refresh AT ALL. It ends
 *     the support session (`api.ts`, `sendWithRetry`).
 *
 * That last one is the important one and it is not merely about token safety.
 * Refreshing on a support 401 would hand back the SUPER ADMIN'S token, and
 * `sendWithRetry` would then replay the request with it — silently re-running a
 * write, a delete included, under a different identity than the one the person
 * believed they were acting as. That is the worst outcome this feature has, and
 * it is why the 401 path ends the session instead of retrying anything.
 */

/**
 * One key, holding one JSON object.
 *
 * Deliberately not four keys: a support session is all-or-nothing, and separate
 * keys can be half-written — a token with no deadline beside it is a session
 * with no end, which is the one state the 30-minute rule exists to prevent.
 */
const SUPPORT_KEY = 'emerge.supportSession';

/** The metadata half — exactly what `useAuth().supportSession` exposes. */
export interface SupportSessionInfo {
  /**
   * The account being acted in.
   *
   * ⚠️ **The NAME does not come from the session record**, whose parties are
   * `{id, email}` and carry none. It comes from the hydrated `user` on the
   * start envelope — the one place the wire spells it out. See
   * `SupportSessionResult` in `types.ts`.
   */
  actingAs: { id: number; name: string; email: string };
  /**
   * The super administrator who started it, as the record names them.
   *
   * ⚠️ **`{id, email}`, and `id` is NULLABLE — no name, deliberately.**
   * `started_by_id` is `ON DELETE SET NULL` while `started_by_email` is the
   * denormalised copy written at the time, so the email is the identity that
   * survives the account. Never gate on the id; it is the half designed to go.
   */
  startedBy: { id: number | null; email: string };
  /** ISO, the server's. Displayed; never the thing compared against. */
  expiresAt: string;
  reason: string;
}

/** What is persisted: the metadata, the token, and the local deadline. */
export interface StoredSupportSession extends SupportSessionInfo {
  /** Short-lived and NON-REFRESHABLE. There is deliberately no refresh token here. */
  accessToken: string;
  /**
   * Epoch ms, on THIS machine's clock.
   *
   * ⚠️ Computed from the server's REMAINING DURATION at start, not by parsing
   * `expiresAt`. See {@link supportDeadlineMs} — the distinction is the whole
   * of the clock-skew handling.
   */
  deadlineMs: number;
}

/**
 * When this support session should end, on the local clock.
 *
 * ## Why not just `Date.parse(expiresAt)`
 *
 * Because that compares the SERVER'S clock to the BROWSER'S, and the difference
 * between them lands directly on the user: a browser five minutes fast ends a
 * 30-minute session at 25 and the super admin is thrown out early for no
 * visible reason; five minutes slow and the app keeps a dead token attached,
 * showing a live banner over requests the server is already refusing.
 *
 * A DURATION has no such problem. `expiresIn` is seconds-remaining as the
 * server counts them, so `now + expiresIn` is skew-free — only drift over the
 * next half hour matters, which is nothing. Same reasoning as `expiresIn` on
 * the auth envelope, and it is why that field was asked for.
 *
 * ✅ **`expiresIn` is REQUIRED on the shipped contract** (confirmed 2026-08-30
 * against `support-sessions.service.ts` and `withExpiresIn` in its repository —
 * it appears on the start envelope AND on the session record, recomputed per
 * call), so the duration branch is the one that runs. The `expiresAt` branch is
 * kept as the documented fallback for an older server; it is skew-dependent,
 * which is a real limitation rather than a rounding detail, and it is why this
 * function must keep PREFERRING the duration rather than being simplified down
 * to whichever key happens to be present.
 *
 * ## Returning null means REFUSE THE SESSION
 *
 * A value that parses to nothing usable yields `null` and the caller must not
 * start. Failing closed is deliberate: a support session whose end nobody can
 * compute is precisely the unbounded access the 30-minute rule forbids, and a
 * start that visibly refuses is reportable — one that quietly never ends is not.
 */
export function supportDeadlineMs(
  result: { expiresAt?: string; expiresIn?: number },
  nowMs: number,
): number | null {
  const { expiresIn, expiresAt } = result;
  // Prefer the duration. `<= 0` is a session that is already over, not a fallback.
  if (typeof expiresIn === 'number' && Number.isFinite(expiresIn)) {
    return expiresIn <= 0 ? null : nowMs + expiresIn * 1000;
  }
  if (typeof expiresAt === 'string' && expiresAt !== '') {
    const parsed = Date.parse(expiresAt);
    if (Number.isFinite(parsed) && parsed > nowMs) return parsed;
  }
  return null;
}

/** Is this record still within its window? Pure, so the timer and the transport agree. */
export function isSupportSessionLive(
  stored: StoredSupportSession | null,
  nowMs: number,
): stored is StoredSupportSession {
  return stored !== null && stored.deadlineMs > nowMs;
}

/** Milliseconds left, floored at 0 — for a countdown that never renders a negative. */
export function supportSessionRemainingMs(
  stored: StoredSupportSession | null,
  nowMs: number,
): number {
  return stored === null ? 0 : Math.max(0, stored.deadlineMs - nowMs);
}

/**
 * Which token a request carries, and whose identity it therefore runs under.
 *
 * Pure and exported so the decision can be reasoned about and driven directly,
 * rather than only being observable through a `fetch`. `activeAccessToken()` is
 * the impure wrapper the transport actually calls.
 */
export function activeTokenOf(
  baseToken: string | null,
  stored: StoredSupportSession | null,
  nowMs: number,
): { token: string | null; actingAs: 'support' | 'self' } {
  if (isSupportSessionLive(stored, nowMs)) {
    return { token: stored.accessToken, actingAs: 'support' };
  }
  return { token: baseToken, actingAs: 'self' };
}

/** Narrow a parsed blob, so a malformed or half-written record can never be used. */
function isStoredSupportSession(value: unknown): value is StoredSupportSession {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  const actingAs = record.actingAs as Record<string, unknown> | undefined;
  const startedBy = record.startedBy as Record<string, unknown> | undefined;
  return (
    typeof record.accessToken === 'string' &&
    record.accessToken !== '' &&
    typeof record.deadlineMs === 'number' &&
    Number.isFinite(record.deadlineMs) &&
    typeof record.expiresAt === 'string' &&
    typeof record.reason === 'string' &&
    typeof actingAs === 'object' &&
    actingAs !== null &&
    typeof actingAs.id === 'number' &&
    typeof actingAs.name === 'string' &&
    typeof actingAs.email === 'string' &&
    typeof startedBy === 'object' &&
    startedBy !== null &&
    // `id` is nullable by contract — the account can be deleted while the
    // denormalised email survives — so `null` must PASS this guard. Requiring a
    // number here would reject a perfectly good record and silently drop the
    // banner off a live session.
    (typeof startedBy.id === 'number' || startedBy.id === null) &&
    typeof startedBy.email === 'string'
  );
}

/**
 * Where the support session lives.
 *
 * Follows `tokenStorage`'s invariant exactly — **write one store, clear the
 * other** — and picks the SAME store the base session uses. That matters: a
 * support session in `localStorage` behind a base session in `sessionStorage`
 * would outlive the tab that owns the account it hangs off, leaving an orphaned
 * token nothing can end.
 */
export const supportSessionStorage = {
  get: (): StoredSupportSession | null =>
    safely(() => {
      const raw = sessionStorage.getItem(SUPPORT_KEY) ?? localStorage.getItem(SUPPORT_KEY);
      if (raw === null) return null;
      const parsed: unknown = JSON.parse(raw);
      if (!isStoredSupportSession(parsed)) {
        // Unreadable is unusable. Drop it rather than leaving it to be re-read.
        supportSessionStorage.clear();
        return null;
      }
      return parsed;
    }, null),

  set: (stored: StoredSupportSession): void =>
    safely(() => {
      const keep = tokenStorage.getRemembered();
      const [target, other] = keep ? [localStorage, sessionStorage] : [sessionStorage, localStorage];
      target.setItem(SUPPORT_KEY, JSON.stringify(stored));
      other.removeItem(SUPPORT_KEY);
    }, undefined),

  clear: (): void =>
    safely(() => {
      localStorage.removeItem(SUPPORT_KEY);
      sessionStorage.removeItem(SUPPORT_KEY);
    }, undefined),
};

type SupportListener = (stored: StoredSupportSession) => void;
const supportEndedListeners = new Set<SupportListener>();

/**
 * Fired when a support session ends for any reason — the clock ran out, the
 * server refused the token, or the super admin pressed the button.
 *
 * `AuthProvider` subscribes and restores the super admin's own identity and
 * permissions. It is an event rather than a return value because two of the
 * three endings happen where nobody is awaiting anything: a timer, and a 401
 * inside the transport.
 */
export function onSupportSessionEnded(listener: SupportListener): () => void {
  supportEndedListeners.add(listener);
  return () => supportEndedListeners.delete(listener);
}

/**
 * End the support session locally, and tell everyone.
 *
 * **Idempotent, and the only way a support session ends.** Idempotence is not
 * decoration: a page firing eight requests at once gets eight 401s at the same
 * instant, and each one arrives here.
 *
 * Deliberately does NOT touch the base tokens, and deliberately does no
 * network. The super admin's own account has been sitting untouched the whole
 * time, so getting back to it is a local delete that cannot fail — which is the
 * property the whole design exists to buy. Telling the server is a separate,
 * fire-and-forget call in `AuthProvider`, on the same reasoning as `logout`.
 */
export function endSupportSessionLocally(): StoredSupportSession | null {
  const stored = supportSessionStorage.get();
  supportSessionStorage.clear();
  if (stored === null) return null;
  for (const listener of supportEndedListeners) listener(stored);
  return stored;
}

/**
 * The token this request should carry.
 *
 * ⚠️ **Ends an expired support session as a side effect, on purpose.** A getter
 * with a side effect is normally wrong; here it is the thing that keeps the
 * screen and the wire from disagreeing. Without it, a tab that slept past the
 * deadline would quietly go back to sending the super admin's token while the
 * banner still said "acting as Priya" — the two facts must move together, and
 * this is the one place both are known.
 */
export function activeAccessToken(): string | null {
  const stored = supportSessionStorage.get();
  if (stored !== null && !isSupportSessionLive(stored, Date.now())) {
    endSupportSessionLocally();
    return tokenStorage.get();
  }
  return activeTokenOf(tokenStorage.get(), stored, Date.now()).token;
}

/**
 * Is a support session live *right now*?
 *
 * Read by the transport BEFORE the request goes out, so that a 401 coming back
 * can be attributed to the right token — the answer must be the one that was
 * true when the header was attached, not when the response landed.
 */
export function hasActiveSupportSession(): boolean {
  return isSupportSessionLive(supportSessionStorage.get(), Date.now());
}

/**
 * The `exp` claim, in epoch seconds, read WITHOUT verifying the signature.
 *
 * Only the server's verification matters for access; this is used purely to
 * decide when to refresh ahead of expiry.
 *
 * `currentAccessExpiry()` is what callers should use: the auth envelope now
 * carries an `expiresIn`, read back out of the freshly signed token
 * server-side so it cannot drift. This decode stays as its fallback, for a
 * session stored before that field existed or a response that omits it. The
 * alternative to both is hardcoding a duration, which would already be wrong:
 * the backend repo's default says 15 minutes and production runs 5.28 days.
 */
export function accessTokenExpiry(token: string | null): number | null {
  if (!token) return null;
  const payload = token.split('.')[1];
  if (!payload) return null;
  return safely(() => {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === 'number' ? exp : null;
  }, null);
}

/**
 * When the current access token expires, in epoch seconds.
 *
 * Prefers the server's `expiresIn`, which it reads back out of the token it
 * just signed — so it cannot drift the way a config value would. Falls back to
 * decoding the JWT for any session that predates that field, or a response
 * that omits it.
 */
export function currentAccessExpiry(): number | null {
  const stored = safely(
    () => sessionStorage.getItem(EXPIRY_KEY) ?? localStorage.getItem(EXPIRY_KEY),
    null,
  );
  const parsed = stored === null ? Number.NaN : Number(stored);
  if (Number.isFinite(parsed)) return parsed;
  return accessTokenExpiry(tokenStorage.get());
}

/* ── Session-ended notification ─────────────────────────────────────────── */

type Listener = () => void;
const expiredListeners = new Set<Listener>();

/**
 * Called when the session is definitively over — the refresh token was
 * rejected. `AuthProvider` subscribes so the app can drop to the login screen
 * from anywhere, including a background refresh no component was awaiting.
 */
export function onSessionExpired(listener: Listener): () => void {
  expiredListeners.add(listener);
  return () => expiredListeners.delete(listener);
}

function endSession(): void {
  tokenStorage.clear();
  /*
   * A support session hangs off the base one and cannot outlive it.
   *
   * Cleared directly rather than through `endSupportSessionLocally()`, because
   * that fires the "go back to your own account" listeners and there is no
   * account left to go back to — the base session is what just died. The
   * expired listeners below drop the whole app to the login screen, which is
   * the honest end for both.
   */
  supportSessionStorage.clear();
  for (const listener of expiredListeners) listener();
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/* ── The refresh itself ─────────────────────────────────────────────────── */

interface RefreshResponse {
  data: { accessToken: string; refreshToken: string; expiresIn?: number };
}

let inFlight: Promise<string | null> | null = null;

/**
 * Exchange the stored refresh token for a new pair.
 *
 * Resolves with a usable access token, or null if the caller should give up —
 * either the session is over (storage is already cleared and listeners have
 * fired) or we are rate-limited and the original failure should stand.
 *
 * Every caller shares one in-flight promise. **Never call this in a retry
 * loop**: a second attempt with a token that already rotated is the replay
 * that revokes every session the user has.
 */
export function refreshSession(): Promise<string | null> {
  inFlight ??= runRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

type Attempt =
  | { kind: 'ok'; accessToken: string }
  /** 401 — either a lost sub-second race or a revoked session. Indistinguishable. */
  | { kind: 'rejected' }
  /** 429 or a network failure: says nothing about the token. Leave the session alone. */
  | { kind: 'inconclusive' };

async function attempt(refreshToken: string): Promise<Attempt> {
  try {
    const response = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // The body is .strict() — one key, exactly. An extra key is a 422.
      body: JSON.stringify({ refreshToken }),
    });

    /*
     * `/auth/refresh` has its own 30-per-15-minutes bucket now, separate from
     * login's 10, so a refresh storm can no longer lock someone out of signing
     * back in. A 429 still says nothing about whether the token is good.
     */
    if (response.status === 429) return { kind: 'inconclusive' };
    if (!response.ok) return { kind: 'rejected' };

    const payload = (await response.json()) as RefreshResponse;
    const { accessToken, refreshToken: rotated, expiresIn } = payload.data;
    /*
     * A grace hit does NOT return the successor the other tab is holding —
     * only a hash of each token is stored, so the raw value exists exactly
     * once. The late caller is answered by rotating that successor and getting
     * ITS replacement. So this is always a genuinely new pair; store it, and
     * whichever tab refreshed last holds the live token.
     */
    tokenStorage.set(accessToken, rotated, undefined, expiresIn);
    return { kind: 'ok', accessToken };
  } catch {
    // Network failure. The token was probably never seen.
    return { kind: 'inconclusive' };
  }
}

/** How long to let the winning tab publish its new pair before retrying. */
const RACE_SETTLE_MS = 400;

async function runRefresh(): Promise<string | null> {
  const previousAccess = tokenStorage.get();
  const refreshToken = tokenStorage.getRefresh();
  if (!refreshToken) {
    // Nothing to rotate. A session predating the refresh flow lands here.
    endSession();
    return null;
  }

  const first = await attempt(refreshToken);
  if (first.kind === 'ok') return first.accessToken;
  if (first.kind === 'inconclusive') return null;

  /*
   * A 401 covers two cases the server deliberately refuses to distinguish — a
   * specific reason would tell an attacker whether a token is unknown, expired
   * or genuinely stolen:
   *
   *   - we lost a SUB-SECOND race and nothing was revoked. Another tab is
   *     about to publish a live pair.
   *   - the token really is dead, and every session for this user is gone.
   *
   * The prescribed handling is the same for both: re-read what the tabs share
   * and try exactly once more. The brief wait exists because the winner writes
   * its pair only when its own request resolves, at roughly the moment ours
   * failed — without it the re-read almost always sees stale data.
   *
   * One retry, never a loop. Past the server's ten-second grace window a reuse
   * is theft again, and a loop is how you get there.
   */
  await sleep(RACE_SETTLE_MS);

  const latestRefresh = tokenStorage.getRefresh();
  if (latestRefresh && latestRefresh !== refreshToken) {
    const second = await attempt(latestRefresh);
    if (second.kind === 'ok') return second.accessToken;
    if (second.kind === 'inconclusive') return null;
  } else {
    // Another tab may have published only the access token we needed.
    const latestAccess = tokenStorage.get();
    if (latestAccess && latestAccess !== previousAccess) return latestAccess;
  }

  endSession();
  return null;
}

/**
 * Revoke the refresh token server-side.
 *
 * Deliberately fire-and-forget and never awaited by the sign-out path: the
 * endpoint answers 204 for any well-formed body — valid token or not — so a
 * non-204 only ever means the request did not arrive. Blocking the redirect on
 * it would leave someone staring at a spinner to be told nothing useful.
 *
 * `keepalive` so it still leaves the browser if the tab is closing.
 */
export function revokeRefreshToken(refreshToken: string | null): void {
  if (!refreshToken) return;
  void safely(
    () =>
      fetch(`${BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
        keepalive: true,
      }).catch(() => undefined),
    undefined,
  );
}
